import { get } from 'lodash';
import { pruneFieldErrors } from '@/utils/errors';
import findResult, { findWithCodes, hasKey } from '@/utils/resultsHelper';
import pagingWithFilteredResults, {
  paging,
  pagingWithResults,
  presentationOptions,
  visibilityFilters,
} from '@/constants/states';
import fields from '@/constants/fields';
import { readStates, states, updateStates } from '@/constants/lifecycle';
import { searchCategories, searchStates } from '../constants';
import sharingSearch, { searchPerms } from '../sharing/state/state';

export const activeSearch = (can = false) => ({
  open: false,
  isNew: true,
  code: null,
  index: null,
  name: '',
  query: '',
  filter: {},
  facets: {},
  description: '',
  type: 'full',
  visibility: 'private',
  permissions: {
    ...searchPerms(can),
  },
  ...searchStates,
});

const openSearch = (can = false) => ({
  id: null,
  code: null,
  name: '',
  query: '',
  filter: {},
  description: '',
  type: 'full',
  visibility: 'private',
  facets: {},
  permissions: {
    ...searchPerms(can),
  },
  ...pagingWithResults(10),
  ...searchStates,
});

export const initialSearchConfig = () => ({
  config: {
    facets: [],
  },
  ...readStates,
  ...updateStates,
});

export const defaultSearch = () => ({
  id: 0,
  isNew: true,
  ...openSearch(true),
});

const searchFlags = (
  datasetsFlag = false,
  linksFlag = false,
  kbFlag = false,
) => ({
  datasetsFlag,
  linksFlag,
  kbFlag,
});

const omniSearch = () => ({
  ...openSearch(true),
  ...paging(5),
  ...searchFlags(true, true, true),
  name: 'omni-search',
});

const homeSearch = () => ({
  ...openSearch(true),
  ...paging(5),
  ...searchFlags(true),
  name: 'home',
});

const savedSearches = (privateFlag, internalFlag, display = presentationOptions.TABLE) => (
  {
    saved: {
      ...pagingWithFilteredResults(),
      ...visibilityFilters(privateFlag, internalFlag),
      display,
      ...searchStates,
    },
  }
);

const initialState = {
  deleteSearchOpen: false,
  sharingSearch: { ...sharingSearch() },
  activeSearch: { ...activeSearch() },
  searchConfig: { ...initialSearchConfig() },
  searches: {
    ...savedSearches(true, false),
    ribbon: {
      params: ['favourites'],
      ...pagingWithFilteredResults(5),
      ...visibilityFilters(),
      ...searchStates,
    },
    open: {
      results: [defaultSearch()],
    },
    deletion: {
      results: [],
    },
    home: {
      results: [homeSearch()],
    },
    omnisearch: {
      results: [omniSearch()],
    },
  },
};

export const pruneSearchErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

const mapFields = (result) => (
  result.dictionary__fields && result.dictionary__fields.length > 0
    ? result.dictionary__fields.map((field) => field.name)
    : null
);

export const foundResult = (result) => ({
  id: result.id,
  code: result.code,
  type: result.type,
  name: result.title,
  description: result.description || '',
  keyword: result.keyword,
  collections: result.collections,
  fields: mapFields(result),
  searchScore: result['@search.score'],
  selected: false,
  visibility: result.settings.visibility,
  requestEnabled: result.request_enabled,
  queryEnabled: result.query_enabled,
  conditionalAccess: result.conditional_access,
  workflowKey: result.settings.workflow_key,
  primaryDictionary: result.primary_dictionary,
  dataModelName: result.data_model_name,
});

export const newSearchProperties = (item, action) => ({
  ...item,
  code: action.isNew ? null : action.code,
  category: action.category,
  isNew: action.isNew,
  open: true,
  ...searchStates,
});

export const checkAuto = (action) => {
  const findState = (action.auto) ? { searchStatus: states.PENDING, searchErrors: [] } : {};
  return {
    ...openSearch(true),
    code: action.code,
    readStatus: states.PENDING,
    readErrors: [],
    ...findState,
  };
};

export const searchStateToLoad = ({ search }) => {
  const items = get(search, 'searches', initialState.searches);
  const savedFilters = items.saved || initialState.searches.saved;

  return {
    ...initialState,
    searches: {
      ...initialState.searches,
      ...savedSearches(savedFilters.privateFlag, savedFilters.internalFlag, savedFilters.display),
    },
  };
};

export const searchStateToSave = ({ search }) => (
  {
    search: {
      searches: {
        saved: {
          privateFlag: search.searches.saved.privateFlag,
          internalFlag: search.searches.saved.internalFlag,
          display: search.searches.saved.display,
        },
      },
    },
  }
);

export const findSearches = ({ searches }, { category }) => searches[category];

export const findAllSearches = ({ searches }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(searches, searchCategories, codes);
};

export const findSearch = ({ searches }, { category, code }) => findResult(searches, category, code);

export const findSearchMembers = (draft, action) => {
  const { unassignedMembers, members } = findSearch(draft, action);
  return action.assignable ? unassignedMembers : members;
};

export default initialState;
